/**
 * OverviewPanel — The Thesis.
 *
 * Frames ILG's central claim: equivalence does not authorize, past proof
 * does not future-prove. CSK proves a static safety claim at fixed
 * regime R; ILG governs whether that claim may continue to be asserted
 * under version evolution. The fibration π_I is the structural form
 * everything else follows from.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { DoctrineManifestSnapshot } from "@csk/demo-ui";
import { PanelSurface, panelTokens } from "@csk/demo-ui";
import { ManuscriptLink } from "./ManuscriptLink.js";
import { DOCTRINE_MANIFEST } from "../data/doctrineManifest.js";

const ACCENT = "#5eead4";

export function OverviewPanel(): React.JSX.Element {
  return (
    <PanelSurface.PanelShell
      title="The Thesis"
      subtitle="Equivalence does not authorize. Past proof does not future-prove."
      accentColor={ACCENT}
    >
      <PanelSurface.ThesisCard
        color={ACCENT}
        glowColor="rgba(94,234,212,0.10)"
        quoteSource="Executive Abstract, ILG v1.0.0"
        quote={
          <>
            ILG converts &ldquo;we proved it once&rdquo; into &ldquo;we can keep
            proving it under change.&rdquo; It replaces checklist governance
            with compositional governance, enforces minimal and controlled
            bridges, governs interpreter drift, and prevents retroactive
            narrative repair of unauthorized changes.
          </>
        }
      />

      <PanelSurface.SectionHeading>Safety surface</PanelSurface.SectionHeading>
      <DoctrineManifestSnapshot manifest={DOCTRINE_MANIFEST} />

      <PanelSurface.Card>
        <PanelSurface.SectionHeading>
          What ILG governs
        </PanelSurface.SectionHeading>
        <p className="mt-3.5 text-[14.5px] leading-[1.7] text-[var(--ilg-text)]">
          CSK provides a <strong>static</strong> boundary neutralization claim
          at a fixed regime <PanelSurface.InlineCode>R</PanelSurface.InlineCode>{" "}
          — &ldquo;no ambient authority,&rdquo; &ldquo;effects are
          bounded,&rdquo; &ldquo;composition preserves invariants.&rdquo; ILG
          provides the <strong>temporal persistence layer</strong> for those
          claims. A typed interface — the{" "}
          <PanelSurface.InlineCode>
            Claim Persistence Functor P_R
          </PanelSurface.InlineCode>{" "}
          — maps static claims into temporal obligations over governed
          evolution.
        </p>
        <p className="mt-2.5 text-[14.5px] leading-[1.7] text-[var(--ilg-text)]">
          ILG does not extend the CSK kernel and does not mix kernel structures.
          It governs <em>when</em> claims may continue to be asserted under
          change.
        </p>
      </PanelSurface.Card>

      <PanelSurface.Card>
        <PanelSurface.SectionHeading>
          Structural form: the fibration
        </PanelSurface.SectionHeading>
        <div className="my-3.5 px-6 py-5 bg-[rgba(6,8,16,0.7)] border border-[var(--ilg-border)] rounded-xl text-center font-mono text-[20px] text-[var(--ilg-bright)]">
          π_I : 𝓔_I &rarr; 𝓥
        </div>
        <PanelSurface.FeatureGrid
          ariaLabel="ILG fibration ingredients"
          minColumnWidth={220}
          items={[
            {
              id: "versions",
              title: <PanelSurface.InlineCode>𝓥</PanelSurface.InlineCode>,
              description:
                "The category of system versions and raw changes (time).",
              color: panelTokens.accentTeal,
            },
            {
              id: "evidence",
              title: <PanelSurface.InlineCode>𝓔_I</PanelSurface.InlineCode>,
              description: "The category of evidence states and witness lifts.",
              color: panelTokens.accentPurple,
            },
            {
              id: "governed-change",
              title: "Governed change",
              description: (
                <>
                  A change is governed iff it admits a valid witness lift in{" "}
                  <PanelSurface.InlineCode>𝓔_I</PanelSurface.InlineCode>.
                </>
              ),
              color: panelTokens.accentGreen,
            },
            {
              id: "composition",
              title: "Temporal integrity",
              description: (
                <>
                  Lift composition closes only when{" "}
                  <PanelSurface.InlineCode>χ₂ ∘ χ₁</PanelSurface.InlineCode>{" "}
                  preserves matching{" "}
                  <PanelSurface.InlineCode>
                    invariant_id
                  </PanelSurface.InlineCode>
                  , <PanelSurface.InlineCode>kind</PanelSurface.InlineCode>, and
                  fingerprint continuity.
                </>
              ),
              color: panelTokens.accentBlue,
            },
          ]}
        />
        <p className="mt-3.5 text-[13px] text-[var(--ilg-muted)] italic leading-[1.6]">
          Governance is therefore compositional, not cumulative. A sequence of
          locally green checks is not, on its own, a global green check.
        </p>
      </PanelSurface.Card>

      <PanelSurface.Card>
        <PanelSurface.SectionHeading>At a glance</PanelSurface.SectionHeading>
        <PanelSurface.KpiGrid minColumnWidth={150} gap={12}>
          <PanelSurface.KpiBox
            label="Seams"
            value={4}
            color={panelTokens.accentRed}
            subtitle="Sᴬ Sᴿ Sᵁ Sᴹ"
          />
          <PanelSurface.KpiBox
            label="Bridges"
            value={3}
            color={panelTokens.accentPurple}
            subtitle="MIG · ICAPExpansion · RegimeMig"
          />
          <PanelSurface.KpiBox
            label="Persistence conditions"
            value={7}
            color={panelTokens.accentTeal}
            subtitle="apex stack"
          />
          <PanelSurface.KpiBox
            label="Theorems + lemmas"
            value={8}
            color={panelTokens.accentBlue}
            subtitle="T1..T3 · L1..L5"
          />
          <PanelSurface.KpiBox
            label="Rulebook codes"
            value={29}
            color={panelTokens.accentAmber}
            subtitle="6 families · fail-closed"
          />
          <PanelSurface.KpiBox
            label="Attack patterns"
            value={9}
            color={panelTokens.accentPink}
            subtitle="A1..A9"
          />
          <PanelSurface.KpiBox
            label="v0 schema files"
            value={15}
            color={panelTokens.accentGold}
            subtitle="14 artifacts + common"
          />
          <PanelSurface.KpiBox
            label="Test vectors"
            value={19}
            color={panelTokens.accentGreen}
            subtitle="valid + invalid"
          />
        </PanelSurface.KpiGrid>
      </PanelSurface.Card>

      <PanelSurface.Card>
        <PanelSurface.SectionHeading>
          How to read this demo
        </PanelSurface.SectionHeading>
        <PanelSurface.StepList
          ariaLabel="Demo navigation guide"
          items={[
            {
              id: "fibration",
              title: "The Fibration",
              description:
                "See π_I in motion. Toggle between a governed lift and three seam refusals.",
              color: panelTokens.accentTeal,
            },
            {
              id: "apex",
              title: "Apex Stack",
              description:
                "The seven persistence conditions, each paired with the rulebook codes it discharges.",
              color: panelTokens.accentPurple,
            },
            {
              id: "seams",
              title: "Seams & Bridges",
              description:
                "The four structural seams and the unique bridge constructor for each.",
              color: panelTokens.accentBlue,
            },
            {
              id: "witness",
              title: "Witness Validator",
              description:
                "Build a PreservationWitness against an IssuerCapability and watch the verdict change as you flip a single field.",
              color: panelTokens.accentGreen,
            },
            {
              id: "composition",
              title: "Composition Lab",
              description:
                "Exercise the Witness Composition Theorem (T3) by chaining χ₂ ∘ χ₁ across a two-step history.",
              color: panelTokens.accentAmber,
            },
            {
              id: "attacks",
              title: "Attack Atlas",
              description:
                "A1..A9, each one a named composite that resolves to specific rulebook firings.",
              color: panelTokens.accentRed,
            },
            {
              id: "theorems",
              title: "Theorems",
              description: "T1..T3, L1..L5 with checkable predicates.",
              color: panelTokens.accentPurple,
            },
            {
              id: "rulebook",
              title: "Rulebook Browser",
              description:
                "All 29 stable codes, filtered by family, severity, or free-text search.",
              color: panelTokens.accentBlue,
            },
            {
              id: "vectors",
              title: "Test Vectors",
              description:
                "The 19 frozen vectors, each replayed through the live validator.",
              color: panelTokens.accentTeal,
            },
            {
              id: "crossmap",
              title: "Cross-Reference Map",
              description:
                "Every code linked to every apex condition, theorem, attack, and seam that cites it.",
              color: panelTokens.accentGold,
            },
            {
              id: "promotion",
              title: "Promotion Gates",
              description: "G0..G3 and the stabilization rule.",
              color: panelTokens.accentGreen,
            },
            {
              id: "boundary",
              title: "Boundary",
              description: "What ILG explicitly does not claim.",
              color: panelTokens.accentPink,
            },
          ]}
        />
        <p className="mt-4 text-[13px] text-[var(--ilg-muted)] leading-[1.6]">
          The full monograph lives at{" "}
          <ManuscriptLink path="theory/manuscripts/contrib/InvariantLifecycleGovernance/">
            theory/manuscripts/contrib/InvariantLifecycleGovernance/
          </ManuscriptLink>
          .
        </p>
      </PanelSurface.Card>
    </PanelSurface.PanelShell>
  );
}
